import { useState } from 'react'
import {
  alpha,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../../providers/SnackbarContext'
import { formatDate } from '../../../../utils/formatting'
import { useAppNavigate } from '../../../../utils/url'
import { TrailSystem } from '../../../../models/TrailSystem.model'
import { TuningProfile } from '../../../../models/TuningProfile.model'
import { TuningProfileService } from '../../../../services/TuningProfileService/TuningProfileService'
import DetailSection from '../../../../components/Sections/DetailSection.component'
import ConfirmDeleteDialog from '../../../../components/DeleteDialog/ConfirmDeleteDialog.component'
import { SectionDefinition } from '../../../../components/Sections/Sections.types'
import TuningProfileNameDialog from './TuningProfileNameDialog.component'
import ApplyTuningProfileDialog from './ApplyTuningProfileDialog.component'

type TuningProfilesSectionProps = {
  section: SectionDefinition
  trailSystem: TrailSystem
  profiles: TuningProfile[]
  isLoading: boolean
  canEdit: boolean
  // called after any change with how the profile list changed, so the page can show it right away and
  // then refetch the trail system and its profiles
  onChanged: (updateProfiles: (profiles: TuningProfile[]) => TuningProfile[]) => void
}

const byName = (a: TuningProfile, b: TuningProfile) => a.name.localeCompare(b.name)
/**
 * @description Lists a trail system's tuning profiles (e.g. Summer, Winter) and lets admins create one
 * from the current values, open one to edit every trail's values, apply one, rename or delete them.
 */
const TuningProfilesSection = (props: TuningProfilesSectionProps) => {
  const { section, trailSystem, profiles, isLoading, canEdit, onChanged } = props
  const activeProfile = profiles.find((profile) => profile.active)

  const [isSaving, setIsSaving] = useState(false)
  const [nameDialog, setNameDialog] = useState<{ mode: 'add' | 'rename'; profile?: TuningProfile } | null>(null)
  const [profileToApply, setProfileToApply] = useState<TuningProfile | null>(null)
  const [profileToDelete, setProfileToDelete] = useState<TuningProfile | null>(null)
  const [menu, setMenu] = useState<{ anchor: HTMLElement; profile: TuningProfile } | null>(null)
  const [isHelpOpen, setIsHelpOpen] = useState(false)

  const tuningProfileService = new TuningProfileService()
  const navigate = useAppNavigate()
  const editorPath = (profile: TuningProfile) => `/trail-systems/${trailSystem.id}/tuning-profiles/${profile.id}`

  const run = <T,>(
    action: () => Promise<T>,
    updateProfiles: (result: T, profiles: TuningProfile[]) => TuningProfile[],
    successMessage: string | ((result: T) => string),
    notice?: string,
    onDone?: (result: T) => void,
  ) => {
    setIsSaving(true)
    action()
      .then((result) => {
        SnackbarContext.show(
          typeof successMessage === 'function' ? successMessage(result) : successMessage,
          'success',
          notice,
        )
        setNameDialog(null)
        setProfileToApply(null)
        setProfileToDelete(null)
        onChanged((currentProfiles) => updateProfiles(result, currentProfiles))
        onDone?.(result)
      })
      .catch((err) => {
        console.error(err)
        SnackbarContext.show(typeof err === 'string' ? err : 'Something went wrong. Please try again.', 'error')
      })
      .finally(() => setIsSaving(false))
  }

  const handleSaveName = (name: string) => {
    const profileToRename = nameDialog?.mode === 'rename' ? nameDialog.profile : undefined
    if (profileToRename) {
      run(
        () => tuningProfileService.rename(profileToRename.id, name),
        (renamed, current) => current.map((profile) => (profile.id === renamed.id ? renamed : profile)).sort(byName),
        `Renamed to ${name}.`,
      )
    } else {
      run(
        () => tuningProfileService.add(trailSystem.id, name),
        // a new profile is only active when it's the system's first one
        (created, current) =>
          [...current.map((profile) => (created.active ? { ...profile, active: false } : profile)), created].sort(
            byName,
          ),
        (created) =>
          created.active
            ? `Created ${created.name}. It's the active profile, so your edits save into it.`
            : `Created ${created.name} from the current values. Edit its values, then apply it when you're ready.`,
        undefined,
        // straight into the editor to tune the new profile
        (created) => navigate(editorPath(created)),
      )
    }
  }

  return (
    <>
      <DetailSection
        section={section}
        action={
          canEdit ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setNameDialog({ mode: 'add' })}
            >
              New Profile
            </Button>
          ) : undefined
        }
      >
        <Stack direction="row" spacing={0.5} alignItems="flex-start">
          <Typography variant="body2" color="grey.400" sx={{ flex: 1 }}>
            Save this system&apos;s tuning values as a profile, then switch every trail at once when the weather turns.
          </Typography>
          <ClickAwayListener onClickAway={() => setIsHelpOpen(false)}>
            <span>
              <Tooltip
                arrow
                open={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
                placement="bottom-end"
                disableFocusListener
                disableHoverListener
                disableTouchListener
                title="Open a profile to edit every trail's values on one page, whether or not it's active. Editing a trail or this system directly saves into the active profile. New trails join every profile."
              >
                <IconButton
                  size="small"
                  aria-label="How tuning profiles work"
                  aria-expanded={isHelpOpen}
                  onClick={() => setIsHelpOpen((prev) => !prev)}
                  sx={{ mt: -0.5, color: 'grey.500' }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </span>
          </ClickAwayListener>
        </Stack>

        {isLoading && (
          <Stack spacing={1} aria-label="Loading tuning profiles" aria-busy>
            <Skeleton variant="rounded" height={64} />
            <Skeleton variant="rounded" height={64} />
          </Stack>
        )}

        {!isLoading && !profiles.length && (
          <Typography variant="body2" color="grey.500">
            No profiles yet. Create one, e.g. Summer, to save the current values and start editing them.
          </Typography>
        )}

        {profiles.length > 0 && (
          <Stack spacing={1}>
            {profiles.map((profile) => (
              <Stack
                key={profile.id}
                role="group"
                aria-label={profile.name}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  // the whole card opens the profile: the name's link stretches over it (see below)
                  position: 'relative',
                  cursor: 'pointer',
                  pl: 2,
                  pr: 1,
                  py: 1.25,
                  bgcolor: 'secondary.main',
                  border: 1,
                  borderColor: profile.active ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  '&:hover, &:focus-within': {
                    backgroundImage: (theme) =>
                      `linear-gradient(${alpha(theme.palette.common.white, 0.05)}, ${alpha(
                        theme.palette.common.white,
                        0.05,
                      )})`,
                    borderColor: profile.active ? 'primary.main' : 'grey.500',
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Link
                      component="button"
                      variant="body1"
                      fontWeight={700}
                      color="inherit"
                      underline="hover"
                      onClick={() => navigate(editorPath(profile))}
                      sx={{
                        overflowWrap: 'anywhere',
                        textAlign: 'left',
                        // stretched over the card so a click anywhere on it opens the profile, while keyboard
                        // and screen reader users still get one real button
                        // MUI makes a button link position: relative, which would shrink the stretch to the name
                        position: 'static',
                        '&::after': { content: '""', position: 'absolute', inset: 0, borderRadius: 1 },
                        '&:focus-visible': { outline: 'none' },
                      }}
                    >
                      {profile.name}
                    </Link>
                    {profile.active ? (
                      <Chip size="small" color="primary" label="Active" />
                    ) : (
                      <Chip size="small" variant="outlined" label="Inactive" />
                    )}
                  </Stack>
                  <Typography variant="caption" color="grey.500">
                    System + {profile.trailCount} {profile.trailCount === 1 ? 'trail' : 'trails'} · saved{' '}
                    {formatDate(profile.updatedAt)}
                  </Typography>
                </Box>
                {canEdit && (
                  <IconButton
                    aria-label={`More actions for ${profile.name}`}
                    onClick={(e) => setMenu({ anchor: e.currentTarget, profile })}
                    // above the card's stretched link so it opens the menu, not the profile
                    sx={{ position: 'relative', zIndex: 1 }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </DetailSection>

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        {menu && !menu.profile.active && (
          // opens the change summary to review before anything is applied
          <MenuItem
            onClick={() => {
              setProfileToApply(menu.profile)
              setMenu(null)
            }}
          >
            Apply Profile
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (menu) {
              navigate(editorPath(menu.profile))
            }
            setMenu(null)
          }}
        >
          Edit Values
        </MenuItem>
        <MenuItem
          onClick={() => {
            setNameDialog({ mode: 'rename', profile: menu?.profile })
            setMenu(null)
          }}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            setProfileToDelete(menu?.profile ?? null)
            setMenu(null)
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <TuningProfileNameDialog
        open={!!nameDialog}
        mode={nameDialog?.mode ?? 'add'}
        trailSystemName={trailSystem.name}
        trailCount={activeProfile?.trailCount}
        initialName={nameDialog?.profile?.name}
        activeProfileName={activeProfile?.name}
        isSaving={isSaving}
        onCancel={() => setNameDialog(null)}
        onSave={handleSaveName}
      />

      <ApplyTuningProfileDialog
        profile={profileToApply}
        trailSystemName={trailSystem.name}
        activeProfileName={activeProfile?.name}
        isApplying={isSaving}
        onCancel={() => setProfileToApply(null)}
        onApply={(profile) =>
          run(
            () => tuningProfileService.apply(profile.id),
            (applied, current) =>
              current.map((eachProfile) =>
                eachProfile.id === applied.id ? applied : { ...eachProfile, active: false },
              ),
            `${profile.name} applied. Statuses update at the next hourly refresh.`,
            APP_UPDATE_NOTICE,
          )
        }
      />

      <ConfirmDeleteDialog
        open={!!profileToDelete}
        title={`Delete ${profileToDelete?.name ?? 'profile'}?`}
        content={
          profileToDelete?.active
            ? `The current values stay as they are, but no profile will be active until you apply one.`
            : `This removes the saved values. The current values aren't affected.`
        }
        onClose={() => setProfileToDelete(null)}
        onDelete={() => {
          if (profileToDelete) {
            run(
              () => tuningProfileService.delete(profileToDelete.id),
              (_, current) => current.filter((profile) => profile.id !== profileToDelete.id),
              `${profileToDelete.name} deleted.`,
            )
          }
        }}
      />
    </>
  )
}
export default TuningProfilesSection
